import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { EventEmitter } from 'events';
import { getLocalSettings } from 'components/settings/localStore';
import { isString } from 'lib/typeInference';

export type IHttpClientConfig = AxiosRequestConfig & {
    version?: string;
};

interface IHttpRequestConfig {
    shouldIncludeAuthToken?: boolean;
    shouldIncludeXApplicationId?: boolean;
    shouldIncludeXClientPlatform?: boolean;
    nonce?: string;
    version?: string;
    /** Skip the global 401-handler for this request. Useful for auth endpoints where a 401
     *  means "wrong credentials" and shouldn't trigger session-expiry recovery (which would
     *  mint a new guest session and wipe the current guest's token). */
    skipUnauthorizedHandling?: boolean;
}

const SKIP_UNAUTHORIZED_KEY = 'pepperSkipUnauthorizedHandling';

interface IHttpRequestArgs<T = Record<string, string>> {
    url: string;
    data?: T;
    headers?: any;
    configs?: AxiosRequestConfig;
    headerConfigs?: IHttpRequestConfig;
}

export class HttpClient {
    public static token = '';
    public static events = new EventEmitter();
    public static merchantId?: string = undefined;
    public defaultRequestHeaderConfigs: IHttpRequestConfig = {
        shouldIncludeAuthToken: true,
        shouldIncludeXApplicationId: true,
        shouldIncludeXClientPlatform: true
    };
    private api: AxiosInstance;
    private BASE_URL = '/';

    constructor({ baseURL, url, version }: IHttpClientConfig) {
        this.api = axios.create({ baseURL, url });
        this.api.interceptors.response.use(
            res => Promise.resolve(res.data),
            err => {
                // A 401 normally means the session has expired — propagate via the global
                // event so the app can recover (e.g. mint a fresh guest session). Callers
                // can opt out by setting `headerConfigs.skipUnauthorizedHandling: true` —
                // typically auth endpoints, where a 401 means "wrong credentials" and the
                // recovery flow would wipe the user's guest token and pending merge state.
                const skipUnauthorized = !!err.config?.[SKIP_UNAUTHORIZED_KEY];
                if (err.response?.status === 401 && !skipUnauthorized) {
                    HttpClient.events.emit('unauthorized', false);
                }
                return Promise.reject(err.response);
            }
        );
        this.BASE_URL = url || '';
        if (isString(version)) {
            this.defaultRequestHeaderConfigs.version = version;
        }
    }

    protected addResponseInterceptor = (
        onFulfilled?: (value: AxiosResponse) => AxiosResponse | Promise<AxiosResponse>,
        onRejected?: (error: any) => any
    ) => {
        this.api.interceptors.response.use(onFulfilled, onRejected);
    };

    protected unauthorize() {
        HttpClient.events.emit('unauthorized', false);
    }

    protected getRequest = <Res>({ url, headers, configs, headerConfigs }: IHttpRequestArgs) => {
        const requestHeaders = { ...this.getRequestHeadersConfig(headerConfigs), ...headers };
        const params = {
            m: configs?.params?.tenantId ? undefined : HttpClient.merchantId,
            ...configs?.params
        };
        return this.api.get<Res, Res>(
            `${this.BASE_URL}${url}`,
            this.buildRequestConfig(headerConfigs, configs, requestHeaders, { params })
        );
    };

    protected postRequest = <Req, Res>({
        url,
        headers,
        configs,
        headerConfigs,
        data
    }: IHttpRequestArgs<Req>) => {
        const requestHeaders = { ...this.getRequestHeadersConfig(headerConfigs), ...headers };
        return this.api.post<Req, Res>(
            url,
            data,
            this.buildRequestConfig(headerConfigs, configs, requestHeaders)
        );
    };

    protected putRequest = <Req, Res>({
        url,
        headers,
        configs,
        headerConfigs,
        data
    }: IHttpRequestArgs<Req>) => {
        const requestHeaders = { ...this.getRequestHeadersConfig(headerConfigs), ...headers };
        return this.api.put<Req, Res>(
            url,
            data,
            this.buildRequestConfig(headerConfigs, configs, requestHeaders)
        );
    };
    protected deleteRequest = <Res>({ url, headers, configs, headerConfigs }: IHttpRequestArgs) => {
        const requestHeaders = { ...this.getRequestHeadersConfig(headerConfigs), ...headers };
        return this.api.delete<unknown, Res>(
            url,
            this.buildRequestConfig(headerConfigs, configs, requestHeaders)
        );
    };

    /** Build the axios config with any opt-out flags surfaced on the config object so the
     *  response interceptor can read them. */
    private buildRequestConfig = (
        headerConfigs: IHttpRequestConfig | undefined,
        configs: AxiosRequestConfig | undefined,
        requestHeaders: Record<string, string>,
        extra?: AxiosRequestConfig
    ): AxiosRequestConfig => {
        const merged: AxiosRequestConfig = { headers: requestHeaders, ...configs, ...extra };
        if (headerConfigs?.skipUnauthorizedHandling) {
            (merged as Record<string, unknown>)[SKIP_UNAUTHORIZED_KEY] = true;
        }
        return merged;
    };

    private getRequestHeadersConfig = (requestHeaderConfigs?: IHttpRequestConfig) => {
        const headers: Record<string, string> = {};
        const _requestHeaderConfigs = {
            ...this.defaultRequestHeaderConfigs,
            ...requestHeaderConfigs
        };

        if (_requestHeaderConfigs.shouldIncludeAuthToken && !!HttpClient.token) {
            headers.Authorization = `Token ${HttpClient.token}`;
        }

        if (!!_requestHeaderConfigs.nonce) {
            headers['x-pepper-req-nonce'] = _requestHeaderConfigs.nonce;
        }

        if (isString(_requestHeaderConfigs.version)) {
            headers['x-api-version'] = _requestHeaderConfigs.version;
        }

        if (!!_requestHeaderConfigs.shouldIncludeXClientPlatform) {
            headers['x-client-platform'] = 'web';
        }

        if (_requestHeaderConfigs.shouldIncludeXApplicationId) {
            const localManifest = getLocalSettings();

            if (!localManifest) {
                throw new Error('x-application-id is missing');
            }

            headers['x-application-id'] = localManifest.applicationId;
        }

        return headers;
    };
}
